import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/group-life-insurance-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

const GROUP_VS_PRIVATE = [
  { factor: 'Coverage Amount', group: '1–2x salary (avg $50K–$100K)', private: 'Up to $5M+', winner: 'private' },
  { factor: 'Portability', group: 'Ends when you leave job', private: 'Stays with you forever', winner: 'private' },
  { factor: 'Rate Increases', group: 'Increases with age/group changes', private: 'Locked in at issue', winner: 'private' },
  { factor: 'Medical Exam', group: 'Usually none (group underwriting)', private: 'Often none (accelerated UW)', winner: 'tie' },
  { factor: 'Monthly Cost', group: 'Often employer-subsidized', private: 'From $10–$75/month', winner: 'tie' },
  { factor: 'Coverage Control', group: 'Employer controls policy', private: 'You control your policy', winner: 'private' },
  { factor: 'Beneficiary Choice', group: 'Limited options', private: 'Any person or entity', winner: 'private' },
  { factor: 'Cash Value', group: 'No', private: 'Available (whole life/IUL)', winner: 'private' },
];

const SUPPLEMENT_REASONS = [
  {
    title: 'Your group coverage is only 1–2x salary',
    desc: 'Most Indianapolis employers offer 1–2x annual salary in group life insurance. For a $75,000 salary, that\'s $75,000–$150,000 in coverage. Financial experts recommend 10–12x salary — meaning you need $600,000–$750,000 more in private coverage.',
    icon: 'ri-bar-chart-line',
    impact: 'Critical Gap',
  },
  {
    title: 'Your coverage ends when you leave your job',
    desc: 'Group life insurance is tied to your employment. If you leave your job, get laid off, or retire, your coverage ends immediately. Private life insurance stays with you regardless of employment status — protecting your family through career changes.',
    icon: 'ri-briefcase-line',
    impact: 'Major Risk',
  },
  {
    title: 'Group rates increase as you age',
    desc: 'Group life insurance rates are not locked in. As you age, your group rates increase — sometimes dramatically. Private term life insurance locks in your rate at the age you apply. A 35-year-old who buys private term locks in their rate for 20–30 years.',
    icon: 'ri-line-chart-line',
    impact: 'Cost Risk',
  },
  {
    title: 'You may not be able to get coverage later',
    desc: 'If you develop a health condition while covered by group life insurance, you may not be able to qualify for private life insurance when you leave your job. Buying private coverage now — while you\'re healthy — protects your insurability.',
    icon: 'ri-heart-pulse-line',
    impact: 'Insurability Risk',
  },
];

const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/business-life-insurance-indianapolis', label: 'Business Life Insurance Indianapolis', icon: 'ri-building-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-shield-check-line' },
];

const FAQS = [
  {
    q: 'Is group life insurance from my Indianapolis employer enough?',
    a: 'For most Indianapolis employees, group life insurance from their employer is NOT enough. Here\'s why: Most Indianapolis employers offer 1–2x annual salary in group life insurance. For a $75,000 salary, that\'s $75,000–$150,000 in coverage. Financial experts recommend 10–12x salary — meaning you need $600,000–$750,000 more in private coverage. Additionally, group life insurance ends when you leave your job. If you develop a health condition while employed and then lose your job, you may not be able to qualify for private life insurance. The solution: supplement your group coverage with a private term life policy while you\'re healthy and rates are low.',
  },
  {
    q: 'What happens to my group life insurance if I leave my Indianapolis employer?',
    a: 'When you leave your Indianapolis employer, your group life insurance coverage ends — typically on your last day of employment or at the end of the month. You may have the option to convert your group policy to an individual policy, but this is usually very expensive (often 3–5x the cost of comparable private coverage) and the coverage amount is limited. The best strategy: buy private life insurance while you\'re employed and healthy. This way, you have coverage that stays with you regardless of your employment status. Call (317) 648-2983 to get a private life insurance quote while your group coverage is still active.',
  },
  {
    q: 'Can Indianapolis small businesses offer group life insurance?',
    a: 'Yes — Indianapolis small businesses can offer group life insurance to their employees. Group life insurance is available to businesses with as few as 2 employees. Benefits for Indianapolis small business owners: 1. Attract and retain top talent — group life insurance is a valued employee benefit. 2. Tax advantages — employer-paid group life insurance premiums are tax-deductible as a business expense. 3. Employees receive up to $50,000 in coverage tax-free. 4. Simple administration — one policy covers all eligible employees. For Indianapolis small businesses, group life insurance typically costs $5–$20 per employee per month. Call (317) 648-2983 to get a group life insurance quote for your Indianapolis business.',
  },
  {
    q: 'How much does group life insurance cost for Indianapolis businesses?',
    a: 'Group life insurance costs for Indianapolis businesses vary based on the number of employees, average age, coverage amount, and industry. Typical costs: Small business (5–25 employees): $5–$15 per employee per month for 1x salary coverage. Medium business (25–100 employees): $4–$12 per employee per month for 1x salary coverage. Large business (100+ employees): $3–$10 per employee per month for 1x salary coverage. For a 20-person Indianapolis business with an average salary of $60,000 and 1x salary coverage: approximately $100–$300/month total. An independent agent can get quotes from multiple group life insurance carriers to find the best rate for your Indianapolis business.',
  },
  {
    q: 'What is the difference between group life insurance and individual life insurance in Indianapolis?',
    a: 'Group life insurance vs. individual life insurance in Indianapolis: Group life insurance: Provided by your employer. Coverage is 1–2x salary (typically $50K–$150K). Ends when you leave your job. Rates may increase over time. You don\'t control the policy. Individual life insurance: You own the policy. Coverage up to $5M+. Stays with you regardless of employment. Rates are locked in at issue. You control all aspects of the policy. The key difference: individual life insurance is portable and permanent. Group life insurance is temporary and tied to your employment. Most Indianapolis financial advisors recommend having both — group coverage as a baseline and individual coverage to fill the gap and provide portability.',
  },
  {
    q: 'Can I keep my group life insurance after retiring in Indianapolis?',
    a: 'In most cases, group life insurance ends when you retire from your Indianapolis employer. Some employers offer retiree life insurance, but coverage is typically reduced (often to $10,000–$25,000) and may be expensive. The best strategy for Indianapolis residents approaching retirement: 1. Buy private life insurance before you retire — while you\'re still healthy and rates are lower. 2. Consider a permanent policy (whole life or universal life) that will last your entire lifetime. 3. If you have health conditions, explore simplified issue or guaranteed issue options. 4. Don\'t wait until retirement to think about this — the older you are, the more expensive private coverage becomes. Call (317) 648-2983 to plan your retirement life insurance strategy.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Michael & Lisa T.',
    location: 'Carmel, Indianapolis',
    quote: 'I had $100K in group life insurance through work and thought that was enough. Benjamin showed me I needed $800K to protect my family. We got $750K private term for $55/month. Eye-opening.',
    stars: 5,
    coverage: '$750K 20-year term',
    situation: 'Supplemented group coverage',
  },
  {
    name: 'Robert K.',
    location: 'Fishers, Indianapolis',
    quote: 'Got laid off and my group life insurance ended the same day. Thankfully I had private coverage Benjamin set up 2 years earlier. That private policy saved my family\'s financial security.',
    stars: 5,
    coverage: '$500K 20-year term',
    situation: 'Coverage survived job loss',
  },
  {
    name: 'Amanda S.',
    location: 'Noblesville, Indianapolis',
    quote: 'I own a small business with 8 employees. Benjamin set up group life insurance for my whole team for $180/month total. My employees love the benefit and it\'s tax-deductible.',
    stars: 5,
    coverage: 'Group plan — 8 employees',
    situation: 'Small business group plan',
  },
  {
    name: 'James & Carol W.',
    location: 'Indianapolis, IN',
    quote: 'Both of us had group life insurance through our jobs. Benjamin showed us we were both underinsured by $600K each. We got private coverage for $43/month each. Best decision we made.',
    stars: 5,
    coverage: '$500K each, 20-year term',
    situation: 'Supplemented both group plans',
  },
];

export default function GroupLifeInsuranceIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [salary, setSalary] = useState('75000');
  const [groupCoverage, setGroupCoverage] = useState('1x');
  const [gapResult, setGapResult] = useState<{ gap: number; recommended: number; group: number } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Group Life Insurance Indianapolis IN | Supplement Your Coverage | Hoosier Life Insurance';
    const description = 'Group life insurance in Indianapolis, IN. Learn why employer coverage is rarely enough. Compare group vs. private rates. Small business group plans available. Call (317) 648-2983.';
    const keywords = 'group life insurance Indianapolis, employer life insurance Indianapolis, supplement group life insurance Indianapolis, small business life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/0f56695009cfb66f24f9320bdd024a80.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Group Life Insurance Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const calculateGap = () => {
    const sal = parseFloat(salary.replace(/,/g, '')) || 75000;
    const multiplier = groupCoverage === '1x' ? 1 : groupCoverage === '2x' ? 2 : 0;
    const group = sal * multiplier;
    const recommended = sal * 10;
    const gap = Math.max(0, recommended - group);
    setGapResult({ gap, recommended, group });
  };

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Group Life Insurance</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20business%20team%20office%20meeting%20discussing%20employee%20benefits%20group%20life%20insurance%20warm%20modern%20office%20natural%20light%20diverse%20team%20smiling%20confident%20photorealistic%20warm%20neutral%20tones%20wide%20angle&width=1440&height=700&seq=group-li-indy-hero-2025a&orientation=landscape"
            alt="Group Life Insurance Indianapolis Indiana — Supplement Your Employer Coverage"
            title="Group Life Insurance in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-building-line text-sm"></i>
              Indianapolis &amp; Marion County · Group vs. Private · Coverage Gap Calculator · Small Business Plans
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Group Life Insurance for{' '}
              <span className="text-[#D97642]">Indianapolis Employers &amp; Teams</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Your employer's group life insurance covers <strong className="text-white">1–2x your salary</strong> — but you need 10x. Use our <strong className="text-white">Coverage Gap Calculator</strong> to see exactly how much private coverage you need to supplement your group plan.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '1–2x', label: 'Typical group life insurance coverage — far below the 10x recommendation' },
                { value: '$0', label: 'Group coverage remaining when you leave your Indianapolis employer' },
                { value: '10x', label: 'Recommended coverage — salary multiplied by 10 years of income' },
                { value: 'Free', label: 'Coverage gap analysis — no obligation, no pressure' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#gap-calculator" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-calculator-line text-lg"></i>
                Calculate My Coverage Gap
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '85%', label: 'Indianapolis employees who are underinsured through group life insurance alone' },
              { value: '$600K', label: 'Average coverage gap for Indianapolis families relying only on group life insurance' },
              { value: '$43/mo', label: 'Cost to fill a $500K coverage gap — 35-year-old, healthy, 20-year term' },
              { value: '$0', label: 'Group coverage remaining the day you leave your Indianapolis employer' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* Coverage Gap Calculator */}
            <div id="gap-calculator" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Coverage Gap Calculator</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                How Much Coverage Are You Missing?
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Enter your salary and group coverage to see your coverage gap — and how much private life insurance you need to fill it.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-calculator-line text-[#D97642]"></i>
                    Your Coverage Profile
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Annual Salary</label>
                    <div className="flex flex-wrap gap-2">
                      {['40000', '50000', '60000', '75000', '100000', '125000', '150000'].map(s => (
                        <button key={s} onClick={() => { setSalary(s); setGapResult(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${salary === s ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          ${parseInt(s).toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Current Group Life Insurance Coverage</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: '0x', label: 'No group coverage' },
                        { value: '1x', label: '1x salary' },
                        { value: '2x', label: '2x salary' },
                        { value: '3x', label: '3x salary' },
                      ].map(opt => (
                        <button key={opt.value} onClick={() => { setGroupCoverage(opt.value); setGapResult(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${groupCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={calculateGap}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-calculator-line text-lg"></i>
                    Calculate My Coverage Gap
                  </button>
                </div>

                {gapResult !== null && (
                  <div className="px-7 py-7">
                    <h3 className="text-base font-bold text-[#1A3A52] mb-6">Your Coverage Analysis</h3>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-gray-400 mb-1">Group Coverage</div>
                        <div className="text-xl font-extrabold text-[#1A3A52]">${gapResult.group.toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">Current employer plan</div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-gray-400 mb-1">Recommended</div>
                        <div className="text-xl font-extrabold text-[#1A3A52]">${gapResult.recommended.toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">10x annual salary</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                        <div className="text-xs text-red-500 mb-1 font-semibold">Coverage Gap</div>
                        <div className="text-xl font-extrabold text-red-500">${gapResult.gap.toLocaleString()}</div>
                        <div className="text-xs text-red-400 mt-1">Private coverage needed</div>
                      </div>
                    </div>

                    {gapResult.gap > 0 && (
                      <div className="bg-[#F0FFF4] border border-[#4A7C59]/20 rounded-xl p-5 mb-6">
                        <div className="flex items-start gap-3">
                          <i className="ri-lightbulb-line text-[#4A7C59] text-lg flex-shrink-0 mt-0.5"></i>
                          <div>
                            <p className="text-sm font-bold text-[#1A3A52] mb-1">
                              You need ${gapResult.gap.toLocaleString()} in private life insurance
                            </p>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              A ${gapResult.gap.toLocaleString()} 20-year term policy for a healthy 35-year-old costs approximately{' '}
                              <strong className="text-[#4A7C59]">${Math.round(gapResult.gap / 500000 * 36)}/month</strong> — less than most streaming subscriptions.
                              This coverage stays with you even if you leave your Indianapolis employer.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Fill My Coverage Gap — Free 15-Min Call
                    </button>
                  </div>
                )}

                {gapResult === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-calculator-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your salary and group coverage above, then click <strong className="text-[#1A3A52]">Calculate My Coverage Gap</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Group vs. Private Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Group vs. Private Life Insurance</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Group Life Insurance vs. Private Life Insurance — Indianapolis
              </h2>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-3 bg-[#1A3A52] px-6 py-3 text-xs font-bold text-white/70 uppercase tracking-wide">
                  <span>Factor</span>
                  <span className="text-center">Group Life Insurance</span>
                  <span className="text-center">Private Life Insurance</span>
                </div>
                {GROUP_VS_PRIVATE.map((row, i) => (
                  <div key={i} className={`grid grid-cols-3 px-6 py-4 border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-sm font-semibold text-[#1A3A52]">{row.factor}</span>
                    <span className={`text-center text-sm ${row.winner === 'private' ? 'text-red-500' : 'text-gray-600'}`}>{row.group}</span>
                    <span className={`text-center text-sm font-bold ${row.winner === 'private' ? 'text-[#4A7C59]' : 'text-gray-600'}`}>{row.private}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Supplement */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why Supplement Your Group Coverage</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                4 Reasons Indianapolis Employees Need Private Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {SUPPLEMENT_REASONS.map((reason, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${reason.icon} text-base text-[#D97642]`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#1A3A52]">{reason.title}</span>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500 whitespace-nowrap">{reason.impact}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{reason.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Employees Who Supplemented Their Group Coverage
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.situation}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Group Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-group-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-building-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Fill Your Coverage Gap</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · No obligation</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free Coverage Gap Analysis</h3>
                    <p className="text-xs text-white/70">15 minutes · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Coverage Gap Analysis',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Current employer coverage or questions? (Optional)',
                    submitButton: 'Confirm My Free Analysis', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Don't rely on your employer's group life insurance alone
          </h2>
          <p className="text-base text-white/75 mb-7">
            Group coverage ends when you leave your job. Private life insurance stays with you forever. Calculate your coverage gap and fill it today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#gap-calculator" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-calculator-line text-lg"></i>
              Calculate My Coverage Gap
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={119} productName="Group Life Insurance Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Group Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Group Life Insurance — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
